export class PDPGallery extends HTMLElement {
  index: number = 0;
  endReached: boolean = false;
  slider: HTMLDivElement | null = null;
  slideCount: number = 0;
  prevButton: HTMLButtonElement | null = null;
  nextButton: HTMLButtonElement | null = null;
  isMobile: boolean = false;

  constructor() {
    super();

    this.slider = this.querySelector('#pdp-gallery');
    if (!this.slider) return;

    this.slideCount = this.slider.children.length;
    if (this.slideCount < 2) return;

    this.isMobile = window.innerWidth < 768;
    this.slider.addEventListener('scroll', this.calculateCurrentIndex);

    this.prevButton = this.querySelector('button[name="pdp-gallery-previous"]');
    this.nextButton = this.querySelector('button[name="pdp-gallery-next"]');

    if (!this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', this.prev);
    this.nextButton.addEventListener('click', this.next);

    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    this.isMobile = window.innerWidth < 768;
    this.calculateCurrentIndex();
  };

  jumpToIndex = (index: number) => {
    if (!this.slider || index < 0 || index >= this.slideCount) return;

    const scrollValue = this.isMobile ? this.computeScrollHeight() * index : this.computeScrollWidth() * index;
    this.scrollToSlide(scrollValue, index);
  };

  computeScrollWidth = () => {
    return this.slider ? this.slider.scrollWidth / this.slideCount : 0;
  };

  computeScrollHeight = () => {
    return this.slider ? this.slider.scrollHeight / this.slideCount : 0;
  };

  calculateCurrentIndex = () => {
    if (!this.slider) {
      this.updateButtons();
      return;
    }

    if (!this.isMobile) {
      const scrollWidth = this.computeScrollWidth();

      this.index = scrollWidth > 0 ? Math.round(this.slider.scrollLeft / scrollWidth) : 0;

      this.endReached =
        this.slider.scrollWidth > this.slider.clientWidth &&
        Math.abs(this.slider.scrollLeft + this.slider.clientWidth - this.slider.scrollWidth) <= 1;
    }

    this.updateButtons();
  };

  updateButtons = () => {
    if (!this.prevButton || !this.nextButton) return;

    const isFirst = this.index === 0;
    const isLast = this.endReached || this.index >= this.slideCount - 1;

    this.prevButton.disabled = isFirst;
    this.nextButton.disabled = isLast;

    this.prevButton.classList.toggle('hidden', isFirst);
    this.prevButton.classList.toggle('s:block', !isFirst);

    this.nextButton.classList.toggle('hidden', isLast);
    this.nextButton.classList.toggle('s:block', !isLast);
  };

  scrollToSlide = (value: number, index: number) => {
    if (!this.slider || index < 0 || index >= this.slideCount) return;

    const direction = this.isMobile ? 'top' : 'left';
    this.slider.scroll({ [direction]: value, behavior: 'smooth' });
    this.calculateCurrentIndex();
  };

  next = () => {
    if (this.index >= this.slideCount - 1) return;

    const nextIndex = this.index + 1;
    const scrollValue = this.isMobile ? this.computeScrollHeight() * nextIndex : this.computeScrollWidth() * nextIndex;
    this.scrollToSlide(scrollValue, nextIndex);
  };

  prev = () => {
    if (this.index <= 0) return;

    const prevIndex = this.index - 1;
    const scrollValue = this.isMobile ? this.computeScrollHeight() * prevIndex : this.computeScrollWidth() * prevIndex;
    this.scrollToSlide(scrollValue, prevIndex);
  };
}

if (!customElements.get('pdp-gallery')) {
  window.customElements.define('pdp-gallery', PDPGallery);
}
